import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ExpandablePackageRow } from "@/components/tenderer/ExpandablePackageRow";

import { 
  Package
} from "lucide-react";

interface TendererAuth {
  email: string;
  accessCode: string;
  companyName: string;
  authenticatedAt: string;
}

interface TenderPackage {
  id: string;
  name: string;
  packageNumber: string;
  description?: string;
  totalFiles: number;
  createdAt: string;
  submissionDeadline?: string;
  sentAt: string;
  status: string;
  hubName?: string;
  projectName?: string;
  shareToken?: string;
}



interface TendererPackagesProps {
  authData: TendererAuth;
}

export function TendererPackages({ authData }: TendererPackagesProps) {
  const { toast } = useToast();
  const [downloadingPackages, setDownloadingPackages] = useState<Set<string>>(new Set());

  // Fetch tender packages
  const { data: packages = [], isLoading, refetch } = useQuery<TenderPackage[]>({
    queryKey: ['/api/tenderer/packages', authData?.email, authData?.accessCode],
    queryFn: () => apiRequest('/api/tenderer/packages', {
      method: 'POST',
      body: JSON.stringify({
        email: authData?.email,
        accessCode: authData?.accessCode
      })
    }),
    enabled: !!authData?.email && !!authData?.accessCode,
    refetchInterval: 30000, // Refresh every 30 seconds to catch status updates
    refetchOnWindowFocus: true, // Refresh when window regains focus
  });

  const handleDownloadPackage = async (pkg: TenderPackage) => {
    setDownloadingPackages(prev => new Set(prev).add(pkg.id));
    try {
      // Track package view when downloading
      const trackResponse = await fetch('/api/tenderer/track-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authData?.email,
          accessCode: authData?.accessCode,
          packageId: pkg.id
        })
      });
      
      if (trackResponse.ok) {
        // Refresh to update status after successful tracking
        await refetch();
      }
      
      // Use the tenderer download endpoint with proper tracking
      const response = await fetch('/api/tenderer/download-package', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packageId: pkg.id,
          email: authData?.email,
          accessCode: authData?.accessCode
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const fileName = `${pkg.packageNumber}_${pkg.name.replace(/[^a-zA-Z0-9]/g, '_')}.zip`;
        
        if (blob.size > 0) {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          
          toast({
            title: "Download successful",
            description: "Package downloaded successfully.",
          });
          
          // Refresh to update status
          refetch();
        } else {
          throw new Error('Download failed: Empty file received');
        }
      } else {
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: `Error: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setDownloadingPackages(prev => {
        const newSet = new Set(prev);
        newSet.delete(pkg.id);
        return newSet;
      });
    }
  };

  const handleViewPackage = async (pkg: TenderPackage) => {
    try {
      // Track package view
      const response = await fetch('/api/tenderer/track-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authData?.email,
          accessCode: authData?.accessCode,
          packageId: pkg.id,
          viewType: 'general'
        })
      });
      
      if (response.ok) {
        // Refresh to update status after successful tracking
        await refetch();
      }
      
      // Open package view in new tab
      window.open(`/package/${pkg.shareToken}`, '_blank');
    } catch (error) {
      console.error('View tracking error:', error);
      // Still open the package even if tracking fails
      window.open(`/package/${pkg.shareToken}`, '_blank');
    }
  };



  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Your Tender Packages</h1>
        <p className="text-sm text-gray-600 mt-1">
          View and download tender packages assigned to your company
        </p>
      </div>



      {/* Table Headers - Responsive and adaptive */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <div className="grid grid-cols-12 gap-1 md:gap-2 lg:gap-3 items-center text-sm font-semibold text-gray-700 w-full">
            <div className="col-span-1"></div> {/* Expand button */}
            <div className="col-span-3 md:col-span-2">Package</div> {/* Reduced from 4/3 to 3/2 */}
            <div className="hidden md:block col-span-2">Hub Name</div> {/* Reduced from lg:col-span-2 to col-span-2 */}
            <div className="col-span-3 md:col-span-2">Project Name</div> {/* Reduced from 3/3/2 to 3/2 */}
            <div className="col-span-1 text-center">Package Files</div>
            <div className="hidden md:block col-span-1 text-center">Received at</div> {/* Hidden on mobile */}
            <div className="hidden md:block col-span-1 text-center">Deadline</div> {/* Hidden on mobile */}
            <div className="col-span-2 text-center">Actions</div> {/* Actions get more space: 2 columns always */}
          </div>
        </div>

        {/* Packages List */}
        <div className="divide-y divide-gray-200">
          {packages.map((pkg) => {
            const isDownloading = downloadingPackages.has(pkg.id);
            
            return (
              <ExpandablePackageRow
                key={pkg.id}
                pkg={pkg}
                authData={authData}
                onDownload={handleDownloadPackage}
                isDownloading={isDownloading}
              />
            );
          })}
          
          {packages.length === 0 && (
            <div className="p-12 text-center">
              <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No packages found</h3>
              <p className="text-gray-500">
                No tender packages have been assigned to your company yet
              </p>
            </div>
          )}
        </div>
      </div>


    </div>
  );
}